import { useState, useEffect, useCallback } from 'react'
import {
  Card, Row, Col, Statistic, Table, DatePicker, Space, Button, Tabs,
  Modal, Form, Input, Select, InputNumber, Tag, Tooltip, message, Popconfirm,
  Typography, Divider
} from 'antd'
import {
  ReloadOutlined, PlusOutlined, DollarOutlined, RiseOutlined,
  FallOutlined, ToolOutlined, TeamOutlined, DeleteOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../services/api'

const { RangePicker } = DatePicker
const { Title, Text } = Typography

// 收支分类
const INCOME_CATEGORIES = [
  { value: 1, label: '销售收入' },
  { value: 11, label: '其他收入' }
]

const EXPENSE_CATEGORIES = [
  { value: 2, label: '采购成本' },
  { value: 4, label: '工资薪金' },
  { value: 5, label: '房租物业' },
  { value: 6, label: '水电费' },
  { value: 7, label: '运杂费' },
  { value: 8, label: '办公费用' },
  { value: 9, label: '营销推广' },
  { value: 10, label: '税费支出' },
  { value: 12, label: '其他支出' }
]

const ALL_CATEGORIES = [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES]

function getCategoryLabel(val: number) {
  return ALL_CATEGORIES.find(c => c.value === val)?.label || '未知'
}

// 颜色
const BRAND_COLOR = '#55266c'

export default function Finance() {
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs()
  ])
  const [dashboard, setDashboard] = useState<any>(null)
  const [transactions, setTransactions] = useState<any[]>([])
  const [txTotal, setTxTotal] = useState(0)
  const [txPage, setTxPage] = useState(1)
  const [txType, setTxType] = useState<string | undefined>(undefined)
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [salaryModalVisible, setSalaryModalVisible] = useState(false)
  const [addForm] = Form.useForm()
  const [salaryForm] = Form.useForm()
  const [salaryEmployees, setSalaryEmployees] = useState<any[]>([])
  const [payday, setPayday] = useState(15)

  // 加载概览数据
  const loadDashboard = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await api.get('/finance-dashboard/dashboard', {
        params: {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD')
        }
      })
      if (res.code === 200) {
        setDashboard(res.data)
      }
    } catch (err) {
      console.error('加载财务概览失败', err)
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  // 加载收支明细
  const loadTransactions = useCallback(async () => {
    try {
      const res: any = await api.get('/finance-dashboard/transactions', {
        params: {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
          type: txType,
          page: txPage,
          page_size: 20
        }
      })
      if (res.code === 200) {
        setTransactions(res.data.list || [])
        setTxTotal(res.data.total || 0)
      }
    } catch (err) {
      console.error('加载收支明细失败', err)
    }
  }, [dateRange, txType, txPage])

  // 加载员工工资设置
  const loadSalarySettings = useCallback(async () => {
    try {
      // 加载所有在职员工
      const res: any = await api.get('/employees', { params: { status: 1, page: 1, page_size: 100 } })
      if (res.code === 200) {
        setSalaryEmployees(res.data?.list || [])
        // 设置工资日
        if (res.data?.list?.[0]?.salary_day) {
          setPayday(res.data.list[0].salary_day)
          salaryForm.setFieldsValue({ payday: res.data.list[0].salary_day })
        }
      }
    } catch (err) {
      console.error('加载员工工资设置失败', err)
    }
  }, [salaryForm])

  // 初始加载数据
  useEffect(() => {
    loadDashboard()
    loadTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (salaryModalVisible) loadSalarySettings()
  }, [salaryModalVisible, loadSalarySettings])

  // 添加手动记录
  const handleAddRecord = async () => {
    try {
      const values = await addForm.validateFields()
      const res: any = await api.post('/finance-dashboard/records', values)
      if (res.code === 200) {
        message.success('记录已添加')
        setAddModalVisible(false)
        addForm.resetFields()
        loadDashboard()
        loadTransactions()
      } else {
        message.error(res.message || '添加失败')
      }
    } catch (err) {
      // validation error
    }
  }

  // 删除手动记录
  const handleDeleteRecord = async (id: number) => {
    try {
      const res: any = await api.delete(`/finance-dashboard/records/${id}`)
      if (res.code === 200) {
        message.success('已删除')
        loadDashboard()
        loadTransactions()
      }
    } catch (err) {
      message.error('删除失败')
    }
  }

  // 保存员工工资设置
  const handleSaveSalary = async () => {
    try {
      // 保存每个员工的工资设置
      let savedCount = 0
      for (const emp of salaryEmployees) {
        const res: any = await api.put(`/employees/${emp.id}`, {
          base_salary: emp.base_salary || 0,
          salary_day: salaryForm.getFieldValue('payday') || 15,
          insurance_fund: emp.insurance_fund || 0,
          commission: emp.commission || 0,
          bonus: emp.bonus || 0,
          other: emp.other || 0
        })
        if (res.code === 200) savedCount++
      }
      message.success(`已保存 ${savedCount} 名员工的工资设置`)
    } catch (err) {
      message.error('保存失败')
    }
  }

  // 生成本月工资
  const handleGenerateSalary = async () => {
    const month = dayjs().format('YYYY-MM')
    try {
      const res: any = await api.post('/finance-dashboard/generate-salary', { month })
      if (res.code === 200) {
        message.success(res.message || '工资已记录')
        setSalaryModalVisible(false)
        loadDashboard()
        loadTransactions()
      } else {
        message.error(res.message || '生成失败')
      }
    } catch (err) {
      message.error('生成工资失败')
    }
  }

  // 更新员工工资字段
  const updateEmployeeField = (index: number, field: string, value: any) => {
    const newEmployees = [...salaryEmployees]
    newEmployees[index] = { ...newEmployees[index], [field]: value }
    setSalaryEmployees(newEmployees)
  }

  // 收支明细表格列
  const columns = [
    {
      title: '日期', dataIndex: 'date', key: 'date', width: 110,
      sorter: (a: any, b: any) => a.date.localeCompare(b.date)
    },
    {
      title: '类型', key: 'type', width: 80,
      render: (_: any, r: any) => (
        <Tag color={r.type === 'income' ? 'green' : 'red'}>
          {r.type === 'income' ? '收入' : '支出'}
        </Tag>
      )
    },
    {
      title: '分类', dataIndex: 'category', key: 'category', width: 100
    },
    {
      title: '来源', key: 'source', width: 100,
      render: (_: any, r: any) => {
        const map: any = {
          order: '订单', monthly_billing: '月结',
          purchase: '采购', manual: '手工'
        }
        const colorMap: any = {
          order: 'blue', monthly_billing: 'purple',
          purchase: 'orange', manual: 'default'
        }
        return <Tag color={colorMap[r.source] || 'default'}>{map[r.source] || r.source}</Tag>
      }
    },
    {
      title: '单号', dataIndex: 'source_no', key: 'source_no', width: 150,
      render: (v: string) => <Text copyable={{ text: v }} style={{ fontSize: 12 }}>{v}</Text>
    },
    {
      title: '金额', key: 'amount', width: 130,
      sorter: (a: any, b: any) => a.amount - b.amount,
      render: (_: any, r: any) => (
        <span style={{
          color: r.type === 'income' ? '#52c41a' : '#ff4d4f',
          fontWeight: 600,
          fontSize: 14
        }}>
          {r.type === 'income' ? '+' : '-'}¥{Number(r.amount || 0).toFixed(2)}
        </span>
      )
    },
    {
      title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true
    },
    {
      title: '操作', key: 'action', width: 70,
      render: (_: any, r: any) => r.source === 'manual' ? (
        <Popconfirm title="确定删除？" onConfirm={() => handleDeleteRecord(r.source_id)}>
          <Button type="link" danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ) : null
    }
  ]

  const summary = dashboard?.summary || {}
  const income = dashboard?.income || {}
  const expense = dashboard?.expense || {}

  return (
    <div style={{ padding: 24 }}>
      {/* 顶部筛选栏 */}
      <Card style={{ marginBottom: 16 }} size="small">
        <Space wrap>
          <span style={{ fontWeight: 500 }}>时间范围：</span>
          <RangePicker
            value={dateRange}
            onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
          />
          <Button icon={<ReloadOutlined />} onClick={() => { loadDashboard(); loadTransactions() }}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: BRAND_COLOR }}
            onClick={() => {
              addForm.resetFields()
              setAddModalVisible(true)
            }}
          >
            添加收支
          </Button>
          <Button
            icon={<TeamOutlined />}
            onClick={() => setSalaryModalVisible(true)}
          >
            工资设置
          </Button>
        </Space>
      </Card>

      {/* 核心统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card hoverable>
            <Statistic
              title={<Text strong style={{ color: BRAND_COLOR }}>总收入</Text>}
              value={Number(summary.total_income || 0)}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#3f8600', fontSize: 24 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card hoverable>
            <Statistic
              title={<Text strong style={{ color: BRAND_COLOR }}>总支出</Text>}
              value={Number(summary.total_expense || 0)}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#cf1322', fontSize: 24 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card hoverable>
            <Statistic
              title={<Text strong style={{ color: BRAND_COLOR }}>净利润</Text>}
              value={Number(summary.net_profit || 0)}
              precision={2}
              prefix="¥"
              valueStyle={{
                color: Number(summary.net_profit || 0) >= 0 ? '#3f8600' : '#cf1322',
                fontSize: 24
              }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card hoverable>
            <Statistic
              title={<Text strong style={{ color: BRAND_COLOR }}>利润率</Text>}
              value={Number(summary.profit_margin || 0)}
              precision={1}
              suffix="%"
              valueStyle={{ fontSize: 24 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 收入/支出分项统计 */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={12}>
          <Card
            title={<span><RiseOutlined style={{ color: '#52c41a' }} /> 收入构成</span>}
            size="small"
          >
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Statistic title="订单收入" value={Number(income.order_income || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 16, color: '#52c41a' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{income.order_count || 0} 笔订单</Text>
              </Col>
              <Col span={12}>
                <Statistic title="月结收入" value={Number(income.monthly_billing_income || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 16, color: '#52c41a' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{income.billing_count || 0} 笔月结</Text>
              </Col>
              <Col span={12}>
                <Statistic title="配送费收入" value={Number(income.delivery_fee_income || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 16, color: '#52c41a' }} />
              </Col>
              <Col span={12}>
                <Statistic title="其他收入" value={Number(income.manual_income || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 16, color: '#52c41a' }} />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={<span><FallOutlined style={{ color: '#ff4d4f' }} /> 支出构成</span>}
            size="small"
          >
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Statistic title="采购成本" value={Number(expense.purchase_cost || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 16, color: '#ff4d4f' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{expense.purchase_count || 0} 笔采购</Text>
              </Col>
              <Col span={12}>
                <Statistic title="工资薪金" value={Number(expense.labor_cost || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 16, color: '#ff4d4f' }} />
              </Col>
              <Col span={12}>
                <Statistic title="房租物业" value={Number(expense.rent_cost || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 16, color: '#ff4d4f' }} />
              </Col>
              <Col span={8}>
                <Statistic title="水电费" value={Number(expense.utility_cost || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 14, color: '#ff4d4f' }} />
              </Col>
              <Col span={8}>
                <Statistic title="其他支出" value={Number(expense.other_cost || 0)} precision={2} prefix="¥" valueStyle={{ fontSize: 14, color: '#ff4d4f' }} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 收支明细 */}
      <Card title="收支明细流水">
        <Tabs
          activeKey={txType || 'all'}
          onChange={(key) => {
            setTxType(key === 'all' ? undefined : key)
            setTxPage(1)
          }}
          items={[
            { key: 'all', label: '全部' },
            { key: 'income', label: '🟢 收入' },
            { key: 'expense', label: '🔴 支出' }
          ]}
        />
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey={(r) => `${r.source}-${r.source_id}`}
          loading={loading}
          pagination={{
            current: txPage,
            total: txTotal,
            pageSize: 20,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setTxPage(p)
          }}
          size="small"
          scroll={{ x: 900 }}
        />
      </Card>

      {/* 添加收支弹窗 */}
      <Modal
        title="添加收支记录"
        open={addModalVisible}
        onOk={handleAddRecord}
        onCancel={() => setAddModalVisible(false)}
        width={500}
        okText="添加"
        cancelText="取消"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="record_type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="收入/支出">
              <Select.Option value={1}>收入</Select.Option>
              <Select.Option value={2}>支出</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.record_type !== cur.record_type}>
            {({ getFieldValue }) => {
              const rType = getFieldValue('record_type')
              const categories = rType === 1 ? INCOME_CATEGORIES : rType === 2 ? EXPENSE_CATEGORIES : ALL_CATEGORIES
              return (
                <Form.Item name="expense_category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
                  <Select placeholder="选择分类">
                    {categories.map(c => (
                      <Select.Option key={c.value} value={c.value}>{c.label}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )
            }}
          </Form.Item>
          <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
            <InputNumber
              min={0.01}
              precision={2}
              style={{ width: '100%' }}
              prefix="¥"
              placeholder="0.00"
            />
          </Form.Item>
          <Form.Item name="transaction_date" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="备注">
            <Input.TextArea rows={2} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 工资设置弹窗 */}
      <Modal
        title="工资设置"
        open={salaryModalVisible}
        onCancel={() => setSalaryModalVisible(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setSalaryModalVisible(false)}>关闭</Button>,
          <Button key="save" type="primary" onClick={handleSaveSalary} style={{ background: BRAND_COLOR }}>
            保存设置
          </Button>,
          <Button key="generate" type="primary" onClick={handleGenerateSalary} style={{ background: '#52c41a' }}>
            记录本月工资
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            按员工设置各项工资，保存后生效。每月工资日自动生成工资记录到财务。
          </Text>
        </div>

        <Form form={salaryForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="payday" label="每月工资日">
            <Select style={{ width: 80 }}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                <Select.Option key={d} value={d}>{d}日</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        <Table
          dataSource={salaryEmployees.map((emp, idx) => ({ ...emp, key: emp.id }))}
          columns={[
            {
              title: '员工',
              dataIndex: 'real_name',
              key: 'real_name',
              width: 100,
              render: (name, record) => <Text strong>{name || '-'}</Text>
            },
            {
              title: '基本工资',
              key: 'base_salary',
              width: 120,
              render: (_, record, idx) => (
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="¥"
                  style={{ width: '100%' }}
                  value={Number(record.base_salary) || 0}
                  onChange={(v) => updateEmployeeField(idx, 'base_salary', v)}
                />
              )
            },
            {
              title: '五险一金',
              key: 'insurance_fund',
              width: 120,
              render: (_, record, idx) => (
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="¥"
                  style={{ width: '100%' }}
                  value={Number(record.insurance_fund) || 0}
                  onChange={(v) => updateEmployeeField(idx, 'insurance_fund', v)}
                />
              )
            },
            {
              title: '提成',
              key: 'commission',
              width: 100,
              render: (_, record, idx) => (
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="¥"
                  style={{ width: '100%' }}
                  value={Number(record.commission) || 0}
                  onChange={(v) => updateEmployeeField(idx, 'commission', v)}
                />
              )
            },
            {
              title: '奖金',
              key: 'bonus',
              width: 100,
              render: (_, record, idx) => (
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="¥"
                  style={{ width: '100%' }}
                  value={Number(record.bonus) || 0}
                  onChange={(v) => updateEmployeeField(idx, 'bonus', v)}
                />
              )
            },
            {
              title: '其它',
              key: 'other',
              width: 100,
              render: (_, record, idx) => (
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="¥"
                  style={{ width: '100%' }}
                  value={Number(record.other) || 0}
                  onChange={(v) => updateEmployeeField(idx, 'other', v)}
                />
              )
            },
            {
              title: '月合计',
              key: 'total',
              width: 110,
              render: (_, record) => {
                const total = Number(record.base_salary || 0) + Number(record.insurance_fund || 0) +
                  Number(record.commission || 0) + Number(record.bonus || 0) + Number(record.other || 0)
                return <Text strong style={{ color: total > 0 ? '#cf1322' : '#999' }}>¥{total.toFixed(2)}</Text>
              }
            }
          ]}
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
          style={{ marginBottom: 16 }}
        />

        <div style={{ padding: '8px 12px', background: '#f6f6f6', borderRadius: 4 }}>
          <Text strong>本月工资合计：</Text>
          <Text style={{ color: '#cf1322', fontSize: 18, fontWeight: 600 }}>
            ¥{salaryEmployees.reduce((s, e) => s + Number(e.base_salary || 0) + Number(e.insurance_fund || 0) +
              Number(e.commission || 0) + Number(e.bonus || 0) + Number(e.other || 0), 0).toFixed(2)}
          </Text>
        </div>
      </Modal>
    </div>
  )
}
